import {RichMessageText, TokenSpan} from "../diagnostics/RichMessageText";
import type {MessageFact, ObjectRef, RichMessage} from "../diagnostics/types";
import {decisionTokenText} from "./decision-steps-text";

export interface DecisionStepBlockProps {
  /** The summary is the block's heading; its facts are the lines under it, in order. */
  message: RichMessage;
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
}

const isCode = (fact: MessageFact) => fact.value?.type === "CODE";
const isFormula = (fact: MessageFact) => !fact.label && isCode(fact);

/** Consecutive formula lines, such as a formula and the numbers plugged into it, share one box. */
const groupRows = (facts: MessageFact[]) => facts.reduce<MessageFact[][]>((groups, fact) => {
  const last = groups[groups.length - 1];
  if (last && isFormula(fact) && isFormula(last[0])) last.push(fact);
  else groups.push([fact]);
  return groups;
}, []);

const Row = ({fact, canNavigate, onNavigate}: {fact: MessageFact} & Pick<DecisionStepBlockProps, "canNavigate" | "onNavigate">) => {
  if (!fact.label)
    return <li className="text-[var(--ots-text)]">{decisionTokenText(fact.value)}</li>;
  return <li className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
    <span className="text-[var(--ots-text-muted)]">{fact.label}</span>
    {/* code values wrap here; the shared token style keeps them on one line */}
    {isCode(fact)
      ? <code className="min-w-0 whitespace-pre-wrap break-words text-right font-mono text-[0.8125rem]">{decisionTokenText(fact.value)}</code>
      : <span className="min-w-0 break-words text-right font-medium text-[var(--ots-text)]">
        {/* plain values take the theme's text color; tones and links use the shared token style */}
        {fact.value?.tone || fact.value?.link ? <TokenSpan token={fact.value} canNavigate={canNavigate} onNavigate={onNavigate}/> : decisionTokenText(fact.value)}
      </span>}
  </li>;
};

/** One heading and its lines: labeled values, formulas in monospace, and plain notes. */
export const DecisionStepBlock = ({message, canNavigate, onNavigate}: DecisionStepBlockProps) => {
  const headed = Boolean(message.summary);
  // a block without a heading keeps the same inset, so its values line up with the headed blocks around it
  return <section className={["border-l-2 pl-3", headed ? "border-[var(--ots-outline-variant)]" : "border-transparent"].join(" ")}>
    {headed && <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
      <RichMessageText message={message} canNavigate={canNavigate} onNavigate={onNavigate}/>
    </h4>}
    {!!message.facts?.length && <ul className="space-y-1 text-sm leading-5">
      {groupRows(message.facts).map((group, index) => isFormula(group[0])
        ? <li key={index} className="space-y-0.5 rounded-md bg-[var(--ots-surface-variant)] px-2 py-1">
          {group.map((fact, line) => <code key={line} className="block whitespace-pre-wrap break-words font-mono text-[0.8125rem] leading-5">{decisionTokenText(fact.value)}</code>)}
        </li>
        : <Row key={index} fact={group[0]} canNavigate={canNavigate} onNavigate={onNavigate}/>)}
    </ul>}
  </section>;
};
